/** @jsxImportSource @emotion/react */
"use client";

import { type Interpolation } from "@emotion/react";
import { useLocale, useTranslations } from "@i18n/react";
import { PopoverClose } from "@radix-ui/react-popover";
import { useState } from "react";

import {
  ChevronDownIcon,
  CloseIcon,
  cssMediaQueryMinWidthTablet,
  IconButton,
} from "../../../../../emotion";
import {
  fontSizes,
  fontWeights,
  lineHeights,
  radius,
  spacing,
  type ThemeType,
} from "../../../../../global/emotion";
import { useIsDesktopViewport } from "../../../../hooks/useIsDesktopViewport";
import { Modal } from "../../modals/Modal/Modal.emotion";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../shadcn/popover.emotion";
import { Button } from "../Button/Button.emotion";
import type { CalendarProps } from "../Calendar/Calendar.emotion";
import { Calendar } from "../Calendar/Calendar.emotion";
import { Text } from "../Text/Text.emotion";

const cssButton: Interpolation<ThemeType> = {
  width: "212px",
  borderRadius: radius.s,
  fontSize: fontSizes.bodyS,
  fontWeight: fontWeights.bodyS,
  lineHeight: lineHeights.bodyS,
};

const cssPopoverContent: Interpolation<ThemeType> = (theme) => ({
  width: "396px",
  padding: `${spacing.m} ${spacing.none}`,
  backgroundColor: theme.semanticColors.background,
  gap: spacing.m,
  display: "flex",
  flexDirection: "column",
});

const cssCalendar: Interpolation<ThemeType> = () => ({
  paddingInline: spacing.none,
  [cssMediaQueryMinWidthTablet]: {
    paddingInline: spacing.l,
  },
});

const cssTitleWrapper: Interpolation<ThemeType> = () => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  paddingInline: spacing.l,
  [cssMediaQueryMinWidthTablet]: {
    justifyContent: "space-between",
  },
});

const modalContent: Interpolation<ThemeType> = () => ({
  padding: `${spacing.m} ${spacing.none}`,
  display: "flex",
  gap: spacing.s,
  flexDirection: "column",
});

type DatePickerProps = Omit<CalendarProps, "selected" | "onSelect" | "mode"> & {
  date: Date | undefined;
  setDate: (date: Date | undefined) => void;
  trigger?: React.ReactNode;
};

export function DatePicker({
  trigger: customTrigger,
  date,
  setDate,
  required,
  ...props
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const isDesktop = useIsDesktopViewport();
  const t = useTranslations();
  const locale = useLocale();

  const trigger = customTrigger ?? (
    <Button variant="outline" css={cssButton} endIcon={<ChevronDownIcon />}>
      {date
        ? date.toLocaleDateString(locale, {
            month: "long",
            year: "numeric",
          })
        : t("pick_a_date")}
    </Button>
  );

  const calendarContent = (
    <>
      <div css={cssTitleWrapper}>
        <Text type="headline" size="s">
          {isDesktop && date
            ? date.toLocaleDateString(locale, {
                month: "long",
                year: "numeric",
              })
            : t("add_session_select_date")}
        </Text>
        {isDesktop && (
          <PopoverClose asChild>
            <IconButton icon={<CloseIcon />} variant="transparentPrimary" />
          </PopoverClose>
        )}
      </div>
      <Calendar
        mode="single"
        required={required}
        selected={date}
        onSelect={(date) => {
          setDate(date);
          setOpen(false);
        }}
        css={cssCalendar}
        defaultMonth={date}
        {...props}
      />
    </>
  );

  if (isDesktop) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent css={cssPopoverContent} align="start">
          {calendarContent}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Modal
      open={open}
      onOpenChange={setOpen}
      trigger={trigger}
      contentCSS={modalContent}
      title={t("add_session_select_date")}
    >
      {calendarContent}
    </Modal>
  );
}
